'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { useCallback, useMemo } from 'react';
import { CheckboxWithLabel } from 'ui-components';
import { useEngine } from '../EngineContext';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import { PRICE_RANGES, getPriceController } from '../facetControllers';
import { useOptimisticFacetSelection } from '../hooks/useOptimisticFacetSelection';
import { useStableFacetValues } from '../hooks/useStableFacetValues';
import { PriceFacetProps } from './PriceFacet.types';

export const PriceFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: PriceFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const priceLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.price.title')?.value ??
    'Price';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.price.clear')?.value ??
    'Clear';

  const eng = useEngine();
  const controller = useMemo(() => getPriceController(eng), [eng]);
  const state = useController(controller);
  const stableValues = useStableFacetValues(
    state.values,
    'ec_price_filter',
    eng
  );
  const { optimisticValues, selectedCount, findStableValue, applyOptimistic } =
    useOptimisticFacetSelection(stableValues);

  const getLabel = (start: number, end: number) => {
    const range = PRICE_RANGES.find((r) => r.start === start && r.end === end);
    if (!range) return `${start}-${end}`;
    const entry = searchLabelsMicrocopy?.find((m) => m.key === range.key);
    return entry?.value ?? range.label;
  };

  const handleClear = useCallback(() => {
    controller.deselectAll();
  }, [controller]);

  const isDark = variant === 'mobile';

  const content = (
    <div className="flex flex-col">
      {optimisticValues.map((facetValue) => (
        <CheckboxWithLabel
          key={`price-${facetValue.start}-${facetValue.end}`}
          id={`price-${facetValue.start}-${facetValue.end}`}
          checked={facetValue.state === 'selected'}
          disabled={
            facetValue.numberOfResults === 0 && facetValue.state !== 'selected'
          }
          onCheckedChange={() => {
            const stableValue = findStableValue(facetValue);
            if (!stableValue) return;
            applyOptimistic([stableValue], () =>
              controller.toggleSelect(stableValue)
            );
            controller.toggleSelect(stableValue);
          }}
          count={facetValue.numberOfResults}
          size="sm"
          isDarkTheme={isDark}
          microcopy={{ label: getLabel(facetValue.start, facetValue.end) }}
        />
      ))}
    </div>
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={priceLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={priceLabel}
      variant={variant}
      className={className}
      onClear={handleClear}
      clearLabel={clearLabel}
      showClear={variant === 'drawer' && selectedCount > 0}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
